import type { HelpContent } from './types';

export const projectTransactionsHelp: HelpContent = {
  pageTitle: 'Project Transactions',
  description: 'A detailed, line-by-line view of all financial transactions across your projects, with filtering, grouping, and export capabilities.',
  functionality: {
    overview:
      'The Project Transactions page gives you a detailed, line-by-line view of all financial transactions across your projects — including revenue, costs, expenses, and purchases. Use it to investigate financial movements, reconcile project accounts, and export transaction data for further analysis.',
    keyFeatures: [
      {
        label: 'Full transaction history',
        detail:
          'View every financial transaction posted against your projects in one place, with full details including date, type, project, amount, and description.',
      },
      {
        label: 'Advanced filtering',
        detail:
          'Filter transactions by project, transaction type, date range, and more to quickly find the records you need.',
      },
      {
        label: 'Field chooser',
        detail:
          'Choose which columns are visible in the transaction list to focus on the information most relevant to your analysis.',
      },
      {
        label: 'Export',
        detail:
          'Export the filtered transaction list to Excel for use in external reporting or reconciliation workflows.',
      },
      {
        label: 'KPI summary',
        detail:
          'A summary bar at the top of the page shows key totals — such as total revenue, total costs, and net — based on your current filter selection.',
      },
      {
        label: 'Grouping',
        detail:
          'Group transactions by project, type, or other dimensions to organise and summarise large volumes of data.',
      },
    ],
    useCases: [
      'Investigating individual financial transactions on a project',
      'Reconciling project costs against budget',
      'Exporting transaction data for external reporting or audit',
      'Reviewing all transactions within a specific date range',
    ],
    sectionsAndFields: [
    {
      name: 'KPI summary bar',
      description:
        'Displays key financial totals based on your current filter selection, including total revenue, total costs, and net position.',
    },
    {
      name: 'Search',
      description:
        'Search across transactions by keywords such as project name, description, or reference.',
    },
    {
      name: 'Filters panel',
      description:
        'A side panel of filter controls. Narrow results by project, transaction type, date range, and other available fields.',
    },
    {
      name: 'Field chooser',
      description:
        'Select which columns are visible in the transaction list to tailor the view to your needs.',
    },
    {
      name: 'Group by',
      description:
        'Group transactions by a selected dimension such as project or transaction type to organise and summarise the list.',
    },
    {
      name: 'Export button',
      description:
        'Exports the current transaction list to Excel, respecting any active filters and column selections.',
    },
    {
      name: 'Transaction list',
      description:
        'The main table displaying all transactions matching your current filters. Columns include date, project, transaction type, description, and financial values.',
    },
    ],
  },
  userGuide: {
    gettingStarted:
      'All transactions visible to you are listed here. Use the filters panel and search to narrow down the list to the transactions you need. The KPI summary bar updates automatically to reflect your current filter selection.',
    steps: [
      {
        title: 'Filter transactions',
        items: [
          { text: 'Click the Filters button to open the filters side panel.' },
          { text: 'Select your desired project, transaction type, or date range.' },
          { text: 'The transaction list and KPI summary will update automatically.' },
        ],
      },
      {
        title: 'Customise your column view',
        items: [
          { text: 'Click the Field Chooser button to open the column selector.' },
          { text: 'Select or deselect columns to show only the information you need.' },
          { text: 'Your preferences are saved automatically.' },
        ],
      },
      {
        title: 'Group transactions',
        items: [
          { text: 'Use the Group By control to select a grouping dimension.' },
          { text: 'Transactions will be organised into collapsible groups with subtotals.' },
        ],
      },
      {
        title: 'Export transactions',
        items: [
          { text: 'Apply any filters you need to scope the export.' },
          { text: 'Click the Export button to download the data as an Excel file.' },
        ],
      },
    ],
    proTips: [
      'Apply date range and project filters before exporting to keep your Excel file focused and manageable.',
      'Use the Group By feature to quickly summarise transaction totals by project or type without needing to export.',
      'The KPI summary bar is a quick way to sanity-check totals before diving into individual transaction lines.',
    ],
  },
  faq: [
    {
      question: 'Why can I only see some transactions?',
      answer:
        'Your transaction visibility is based on your access permissions. If you believe you are missing visibility of certain transactions, contact your Administrator to review your project access.',
    },
    {
      question: 'Can I edit transactions from this page?',
      answer:
        'No. The Project Transactions page is read-only. Transactions are posted from other areas of Access Projects such as project billing, expenses, and purchases.',
    },
    {
      question: 'Why are the KPI totals not what I expected?',
      answer:
        'The KPI summary reflects your current filter selection only. Check that your date range and project filters are set correctly to capture all the transactions you expect.',
    },
    {
      question: 'What file format does the export produce?',
      answer:
        'Transactions are exported in Excel format.',
    },
    {
      question: 'Can I see transactions for all projects?',
      answer:
        'Your view is scoped to the projects you have access to. If you need visibility of additional projects, contact your Administrator.',
    },
  ],
  relatedPages: [
    { label: 'Projects', path: '/projects' },
    { label: 'WIP', path: '/reports/wip' },
    { label: 'Project P&L', path: '/reports/project-pl' },
  ],
};
